import {Injectable, NgZone} from '@angular/core';
import {HttpInterceptorService} from '../../../core/services/http-interceptor.service';
import {InterpolateService} from '../../services/interpolate.service';
import {catchError, map, shareReplay, switchMap, tap} from 'rxjs/operators';
import {isObservable, Observable, of, ReplaySubject, Subject, timer} from 'rxjs';
import {HttpErrorResponse} from "@angular/common/http";
import {IspmHttpClient} from "@ispm/core/services/ispm-http-client";
import {searchConstants} from "./search.constants";
import {CoreActionCreatorService} from "@ispm/core/services/core-action-creator.service";
import {get, isEmpty, set} from 'lodash';
import {Store} from "@ngrx/store";
import {IgenomicsState} from "@ispm/core/interfaces/genomics-state.interface";
import {NavigationConfigService} from "@ispm/navigation/services/navigationConfig.service";
import {AuthenticationService} from '@ispm/user/services/authentication.service';
import { RouterManagerService } from '@ispm/core/services/router-manager.service';

@Injectable()
export class SearchService {

  public organization: string;
  private cached: Map<string, Subject<any>> = new Map<string, ReplaySubject<any>>();
  private includeSearchTermForCache: boolean = false;
  private requestsSet: Set<string> = new Set<string>();
  private languageSelected: string;
  constructor(private _isgHttpClient: HttpInterceptorService,
              private interpolator: InterpolateService,
              private authService: AuthenticationService,
              private _zone: NgZone,
              private _ispmHttpClient: IspmHttpClient,
              private coreAC: CoreActionCreatorService,
              private genomicsStore: Store<IgenomicsState>,
              private navigationConfigService: NavigationConfigService,
              private _route: RouterManagerService) {

    this.genomicsStore.select('users','organizationConfiguration').subscribe(orgConfig => {
      this.languageSelected = get(orgConfig,'language.code','en-US');
    });
        _zone.runOutsideAngular(() => {
            timer(0, searchConstants.BUFFER_TIME_MILLISECONDS).subscribe(() => {
                _zone.run(() => {
                    if (!isEmpty(this.requestsSet)) {
                        _zone.run(() => {
                            const queries = Array.from(this.requestsSet);
                            this.callServer(queries);
                        });
                    }
                });
            });
        });
  }

  public query(resource, queryString, searchObject = {}, encodeQueryParam = false, attributes = {}
    , useOriginalOrgId: boolean = false) {
    if(!resource || resource === "null") { return Observable.of(); }
    queryString = encodeQueryParam ? encodeURIComponent(queryString) : queryString;
    searchObject['organization'] = searchObject['organization'] || this.organization || this.authService.user.organization;
    searchObject['searchTerm'] = Object.assign(queryString, searchObject); // backward compatible

    // add parameter to search.term to use in requests
    set(searchObject,'searchTerm.queryString',queryString);

    searchObject['appId'] = this.navigationConfigService.getCurrentApplicationId();
    searchObject['urlParams'] = { ...this._route.parameters };
    const resourceArray = resource.split('|');
    const query = this.interpolator.interpolate(resourceArray[0], searchObject, true);
    return this.fromCache(query, resourceArray[0], useOriginalOrgId, attributes, queryString);
  }

  public registerQuery(query: string, disableCache: boolean): Observable<any> {
    const customQuery = this.customQuery(query);
    if(!this.cached.has(customQuery) || disableCache){
        this.requestsSet.add(query);
        const subject = new ReplaySubject(1);
        this.cached.set(customQuery, subject);
    }
    return this.cached.get(customQuery).asObservable();
  }

  public getTransformObject(resource) {
    const transformObject = { display: 'display', code: 'code', shortName: null, code1: null};
    if (!resource || resource === "null") { return transformObject; }
    const fieldsSection = resource.split('|')[1] || null;
    if (fieldsSection === 'transform=IGNORE') {
      return null;
    }
    const fieldsRegexResults = searchConstants.FIELDS_REGEX.exec(fieldsSection);
    if (!(fieldsSection && fieldsRegexResults && fieldsRegexResults.length > 2 && fieldsRegexResults[2])) {
      return transformObject;
    }
    const fields = fieldsRegexResults[2].split(',');
    for (const field of fields) {
      const currentField = field.split(':');
      transformObject[currentField[1]] = this.getFieldFromQueryString(currentField[0]);
    }
    return transformObject;
  }

  public formalize(resource: string, element: any) {
    const fieldsSection = resource.split('|')[1] || null;
    const fieldsRegexResults = searchConstants.FIELDS_REGEX.exec(fieldsSection);
    if (fieldsSection && fieldsRegexResults && fieldsRegexResults.length > 2 && fieldsRegexResults[2]) {
      const field = fieldsRegexResults[fieldsRegexResults.length - 1].split(',')
        .map(field => field.split(':'))
        .find(field => field[1].toLowerCase() === 'display');
      element[this.getFieldFromQueryString(field[0])] = eval(`element.${field[0]}`);
    }
    return element;
  }

  private getFieldFromQueryString(field) {
    const regexResult = searchConstants.ARRAY_REGEX.exec(field);
    return regexResult && !searchConstants.ARRAY_REGEX.test(regexResult[regexResult.length - 1])
      ? regexResult[regexResult.length - 1] || 'display'
      : field;
  }

  private customQuery(query: string): string {
    if (query.search(searchConstants.ISPM_REGEX) > -1) {
      query = query.split(searchConstants.ISPM_REGEX)[1];
    }
    return query;
  }

  /**
   * Below logic will cache the values set call if search term is empty
   */
  private fromCache(query: string, resourceString: string, useOriginalOrgId: boolean = false, attributes: any, queryParam: string): Observable<any> {
    const cacheEmptySearch: boolean = attributes.cacheEmptySearch == false ? false : true; //default true
    const sort: boolean = attributes.sort === true ? true : false; // default false
    query = this.addLocaleIdParamsToURL(query,sort)
    if (this.includeSearchTermForCache ||
        !(InterpolateService.hasInterpolationString(resourceString)) ||
        (isEmpty(queryParam) && cacheEmptySearch)){ //search will be cached even when search parameter is define but it is empty (and it is configured to cache in case of empty search).
      const customQuery = this.customQuery(query);
      const disableCache = attributes['[forceFetch]'];
      if (!disableCache && this.cached.has(customQuery)) {
        return isObservable(this.cached.get(customQuery)) ? this.cached.get(customQuery) : this.cached.get(customQuery).asObservable();
      } else {
        let response$;
        const skipBundle = attributes['[skipBundle]'];
        if (skipBundle || useOriginalOrgId) {
          const httpCall = this.sendQueryByHandler(query, useOriginalOrgId);
          const timer$ = timer(0, searchConstants.DEFAULT_EXPIRY_DURATION_MILLISECONDS);
          response$ = timer$.pipe(
            tap(() => {
              this._zone.run(() => { // Bring back to zone
              })
            }),
            switchMap(() => {
              return httpCall.pipe(catchError((err: HttpErrorResponse) => {
                console.error(err);
                return of(null);
              }))
            }),
            shareReplay(5)
          );
          this.cached.set(query, response$);
          this._zone.runOutsideAngular(() => { // for Automation not to stuck
            response$.publish().connect(); // Firing
          });
          return response$;
        } else {
          return this.registerQuery(query, disableCache);
        }
      }
    } else {
      return this.sendQueryByHandler(query, useOriginalOrgId);
    }
  }

  private sendQueryByHandler(query, useOriginalOrgId): Observable<any> {
    let handler;
    [handler, query] = this._getHandler(query);
    return handler.get(query, {}, false, {}, {}, false, true, useOriginalOrgId)
      .pipe(map((res: any) => 'json' in res ? res.json() : res))
  }

  private _getHandler(query: string): [any, string] {
    if (query.search(searchConstants.ISPM_REGEX) > -1) {
      query = query.split(searchConstants.ISPM_REGEX)[1];
      return [this._ispmHttpClient, query];
    } else {
      return [this._isgHttpClient, query];
    }
  }

  private bulkSearchUntilExpiryDuration(httpCall) {
        return httpCall.pipe(catchError((err: HttpErrorResponse) => {
          console.error('search service - bulk search error ', err);
          return of(err);
    }));
  }

  private bulkSearchCallISG(payload): Observable<any> {
      const httpCall = this._isgHttpClient.post(searchConstants.BULK_SEARCH_END_POINTS, payload);
      return this.bulkSearchUntilExpiryDuration(httpCall);
  }

  private bulkSearchCallISPM(payload): Observable<any> {
      const httpCall = this._ispmHttpClient.post(searchConstants.BULK_SEARCH_END_POINTS, payload);
      return this.bulkSearchUntilExpiryDuration(httpCall);
  }

  private callISGServer(queries: Array<string>) {
    this._zone.runOutsideAngular(() => { // for Automation not to stuck
      this.bulkSearchCallISG({queries: queries}).subscribe(response => {
        this.checkResponseBodyAndUpdateCache(response, queries, searchConstants.ISG_SERVER);
        },
        (error) => {
          this.coreAC.setErrorModal(true, searchConstants.PARTIAL_INFORMATION, searchConstants.PARTIAL_INFORMATION_FULL_MESSAGE, searchConstants.RELOAD);
        });
    });
  }

  private callISPMServer(queries: Array<string>) {
    this._zone.runOutsideAngular(() => { // for Automation not to stuck
      this.bulkSearchCallISPM({queries: queries}).subscribe(response => {
          this.checkResponseBodyAndUpdateCache(response, queries,searchConstants.ISPM_SERVER);
        },
        (error) => {
          this.coreAC.setErrorModal(true, searchConstants.PARTIAL_INFORMATION, searchConstants.PARTIAL_INFORMATION_FULL_MESSAGE, searchConstants.RELOAD);
        });
    });
  }

  private callServer(queries: Array<string>): void {
    const orderedCalls = this.extractCalls(queries);
    if (!isEmpty(orderedCalls.isg)) {
      this.callISGServer(orderedCalls.isg);
    }
    if (!isEmpty(orderedCalls.ispm)) {
      this.callISPMServer(orderedCalls.ispm);
    }
  }


  private cacheUpdate(data: any, queries: Array<string>, server): void {
    let emptyDataQueris = [];
    queries.forEach((query) => {
      const valuesetData = data[query];
      if (valuesetData === "timeout") {
        emptyDataQueris.push(query);
      } else {
        if (this.cached.has(query) && valuesetData) {
          setTimeout(() => {
            this.cached.delete(query);
          }, searchConstants.DEFAULT_EXPIRY_DURATION_MILLISECONDS);
          this.cached.get(query).next(valuesetData);
        } else {
          this.coreAC.setErrorModal(true, searchConstants.PARTIAL_INFORMATION, searchConstants.PARTIAL_INFORMATION_FULL_MESSAGE, searchConstants.RELOAD);
        }
      }
    });
    // retry timeout queries again to get data
    if (emptyDataQueris.length > 0) {
      this.callResepectiveServer(server, emptyDataQueris);
    }
  }

  private extractCalls(calls: Array<string>): {isg: Array<string>, ispm: Array<string>} {
    let orderCalls = {isg: [], ispm: []};
    calls.forEach((call) => {

      if(this.requestsSet.has(call)){
          this.requestsSet.delete(call);
      }
     if (call.search(searchConstants.ISPM_REGEX) === -1) {
        orderCalls.isg.push(call);
      } else {
        call = call.split(searchConstants.ISPM_REGEX)[1];
        orderCalls.ispm.push(call);
      }
    });
    return orderCalls;
  }

  public toggleIncludeSearchTermForCache() {
    this.includeSearchTermForCache = !this.includeSearchTermForCache;
  }
// Passing optional parameter to check sorting is enabled or not
  protected addLocaleIdParamsToURL(url:string,sort?: boolean): string {
    let orgLanguage = this.languageSelected;
    const localeParams = orgLanguage === HttpInterceptorService.DEFAULT_ORGANIZATION_LANGUAGE ?  HttpInterceptorService.DEFAULT_ORGANIZATION_LANGUAGE : `${orgLanguage},${HttpInterceptorService.DEFAULT_ORGANIZATION_LANGUAGE}`
    if ((url.toLowerCase().indexOf (HttpInterceptorService.VALUESET) || url.toLowerCase().indexOf(HttpInterceptorService.VALUESET_FLATTEN_AND_FILTER)) !== -1) {
      if(sort) { // We can configure this in form attribute to apply sorting
        url = url.includes('?') ?  `${url}&locale=${localeParams}&_sort:asc=display` : `${url}?locale=${localeParams}&_sort:desc=locale`
      } else {
        url = url.includes('?') ?  `${url}&locale=${localeParams}&_sort:desc=locale` : `${url}?locale=${localeParams}&_sort:desc=locale`
      }
    }
    return url;
  }

  public clearKeyFromCache(query:string){
    this.cached.delete(query);
  }

  /**
   * Check if response body has data otherwise retry failed queries
   * @param resp
   * @param queries
   * @param server
   * @private
   */
  private checkResponseBodyAndUpdateCache(resp, queries, server) {
    if (resp && resp.hasOwnProperty('status') && get(resp, 'status') === 504) {
      this.callResepectiveServer(server, queries);
    } else if (resp) {
      let data;
      if (resp.body) {
        data = resp.body;
      } else if (Object.keys(resp).length > 0) {
        data = resp;
      }
      this.cacheUpdate(data, queries, server);
    }
  }

  /**
   *
   * @param server
   * @param queries
   * @private
   */
  private callResepectiveServer(server, queries) {
    if(server === searchConstants.ISG_SERVER)
      this.callISGServer(queries);
    if(server === searchConstants.ISPM_SERVER)
      this.callISPMServer(queries);
  }
}
